/**
 * The bottom of the HUD, three places. In the middle: the Place button while placing, else the
 * main button of what stands under me (Harvest on a machine, the hub's card). At the left, mirroring
 * Build at the right: the Values of what stands under me, its gauges live on the button.
 */
import type { JSX } from 'react';

import { currentOptions } from '@game/options/options';
import { factsHere } from '@game/planet/here';
import { BuildButton } from '@ui/features/build/BuildButton';
import { OptionBar } from '@ui/features/options/OptionBar';
import { isSideOption, SideOption } from '@ui/features/options/SideOption';
import { PlacementBar } from '@ui/features/placement/PlacementBar';
import { useGame, useGameState, useLocale, useTick } from '@ui/hooks';

export function BottomBar(): JSX.Element | null {
  useLocale();
  const game = useGame();
  const placing = useGameState((s) => s.placing);
  // what the bar shows follows the tile under me, the planet and the queue
  useGameState((s) => s.here);
  useGameState((s) => s.planetVersion);
  useGameState((s) => s.pending.length);
  useGameState((s) => s.predicting);
  const options = placing ? [] : currentOptions(game);
  const here = factsHere(game);
  // what an extractor holds grows with time, not with the store; a charge's fuse burns down
  useTick(
    here?.machine?.role === 'charge'
      ? 250
      : here?.machine?.role === 'infuser' || options.some((o) => o.kind === 'collect')
        ? 1000
        : null,
  );
  if (!here) return null;
  const side = options.filter(isSideOption);
  const middle = options.filter((o) => !isSideOption(o));
  return (
    <div className="bar">
      <div className="side left">
        {side.map((option) => (
          <SideOption key={option.kind} option={option} here={here} />
        ))}
      </div>
      <div className="middle">
        {placing ? (
          <PlacementBar blueprint={placing} />
        ) : (
          middle.length > 0 && <OptionBar options={middle} here={here} />
        )}
      </div>
      <div className="side right">
        <BuildButton />
      </div>
    </div>
  );
}
